import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, ExternalLink, Send, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SubmitArtist = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ artist_name: "", apple_music_url: "", email: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const change = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.artist_name.trim() || !form.email.trim()) {
      toast.error("Artist name and email are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/artists/submit", form);
      setSubmitted(r.data);
      toast.success("Submission received!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="submit-artist-page" className="max-w-xl mx-auto pt-12 pb-24">
        <div className="rounded-3xl p-10 bg-gradient-to-br from-[#FF2E00]/15 via-[#FF2E00]/10 to-[#E2FF31]/10 border border-white/10 text-center">
          <CheckCircle2 size={56} className="mx-auto text-[#E2FF31] mb-4" />
          <h1 className="font-display text-3xl font-semibold mb-3">Submission received!</h1>
          <p className="text-white/70 mb-4">
            {submitted.matched_to_itunes ? (
              <>We matched your submission to <span className="text-white font-semibold">{submitted.matched_artist_name}</span> on Apple Music. Our team will review it within 7 days.</>
            ) : (
              <>We couldn&apos;t auto-match your artist on Apple Music. Our team will manually review your submission and reach out.</>
            )}
          </p>
          <p className="text-sm text-white/40 mt-2">Submission ID: <code className="font-mono text-[#FF2E00]">{submitted.submission_id}</code></p>
          <button
            data-testid="submit-another-btn"
            onClick={() => { setSubmitted(null); setForm({ artist_name: "", apple_music_url: "", email: "", bio: "" }); }}
            className="mt-6 bg-white/10 hover:bg-white/20 text-white rounded-full px-5 py-2.5 text-sm transition"
          >
            Submit another artist
          </button>
          <button
            onClick={() => navigate("/")}
            className="mt-6 ml-3 bg-white text-black font-semibold rounded-full px-5 py-2.5 text-sm hover:scale-[1.03] transition"
          >
            Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-artist-page" className="max-w-2xl mx-auto pb-24">
      <div className="relative -mx-4 sm:-mx-8 px-4 sm:px-8 py-12 mb-8 overflow-hidden rounded-b-3xl">
        <div className="absolute inset-0 -z-10" style={{ background: "radial-gradient(700px circle at 30% 30%, rgba(255,51,102,0.25), transparent 60%), radial-gradient(500px circle at 80% 70%, rgba(226,255,49,0.12), transparent 60%), #050505" }} />
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-semibold mb-3 flex items-center gap-2">
          <Mic size={12} className="text-[#E2FF31]" /> Discovery Pipeline
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter leading-none">Submit your artist</h1>
        <p className="text-white/65 mt-4 max-w-xl">
          Tell us about an emerging artist (you or someone you love) and we&apos;ll consider them for our
          <span className="text-[#E2FF31] font-semibold"> Trending New Artists</span> rotation
          and the <span className="text-[#FF2E00] font-semibold">Top 100 Rising</span> boost.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-2">Artist name <span className="text-[#FF2E00]">*</span></label>
          <input
            data-testid="submit-name-input"
            value={form.artist_name}
            onChange={change("artist_name")}
            placeholder="e.g. Olivia Dean"
            className="w-full bg-white/[0.04] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-3 outline-none transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <ExternalLink size={14} className="text-white/40" />
            Apple Music / iTunes artist URL
            <span className="text-xs text-white/40 font-normal">(helps us auto-verify)</span>
          </label>
          <input
            data-testid="submit-url-input"
            value={form.apple_music_url}
            onChange={change("apple_music_url")}
            placeholder="https://music.apple.com/us/artist/artist-name/123456"
            className="w-full bg-white/[0.04] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-3 outline-none transition font-mono text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Your email <span className="text-[#FF2E00]">*</span></label>
          <input
            data-testid="submit-email-input"
            type="email"
            value={form.email}
            onChange={change("email")}
            placeholder="you@example.com"
            className="w-full bg-white/[0.04] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-3 outline-none transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Quick bio <span className="text-white/40 font-normal text-xs">(500 chars max)</span></label>
          <textarea
            data-testid="submit-bio-input"
            value={form.bio}
            onChange={change("bio")}
            maxLength={500}
            rows={4}
            placeholder="Tell us what makes this artist worth discovering &mdash; their sound, why people are starting to talk about them..."
            className="w-full bg-white/[0.04] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-3 outline-none transition resize-none"
          />
          <p className="text-xs text-white/40 mt-1 text-right">{form.bio.length} / 500</p>
        </div>

        <button
          data-testid="submit-artist-btn"
          type="submit"
          disabled={busy}
          className="w-full sm:w-auto bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-7 py-3 flex items-center justify-center gap-2 transition disabled:opacity-50 hover:shadow-[0_0_24px_rgba(255,51,102,0.4)]"
        >
          {busy ? "Submitting..." : (<><Send size={16} /> Submit for review</>)}
        </button>
        <p className="text-xs text-white/40 mt-3 flex items-start gap-2">
          <Sparkles size={12} className="text-[#E2FF31] mt-0.5" />
          Approved submissions get featured in our Trending New Artists row and earn a guaranteed slot in the Top 100 with a 🔥 RISING badge.
        </p>
      </form>
    </div>
  );
};

export default SubmitArtist;
